export const ARROW_SIZE = 48;

export type HorizontalAnchor = 'left' | 'center' | 'right';
export type VerticalAnchor = 'top' | 'center' | 'bottom';

export interface ArrowPosition {
  horizontal: HorizontalAnchor;
  vertical: VerticalAnchor;
  /** Distance in px from the anchored horizontal edge. Ignored when `horizontal` is `center`. */
  offsetX: number;
  /** Distance in px from the anchored vertical edge. Ignored when `vertical` is `center`. */
  offsetY: number;
}

/**
 * Splits a screen axis into three equal sections and returns which one the coordinate lands in:
 * 0 — first section (near the low edge), 1 — center section, 2 — last section (near the high edge).
 */
const pickSection = (coordinate: number, axisSize: number): 0 | 1 | 2 => {
  if (coordinate < axisSize / 3) return 0;
  if (coordinate < (axisSize * 2) / 3) return 1;
  return 2;
};

const clampOffset = (value: number, maxOffset: number) => Math.max(0, Math.min(value, Math.max(0, maxOffset)));

/**
 * Derives an edge-relative anchor from the button's current rect. The section the button's center
 * falls into decides the anchor on each axis; when it is the center section the button snaps to the
 * middle of that axis, otherwise its distance to the nearest edge is recorded.
 */
export const computeArrowPosition = (rect: DOMRect, viewportWidth: number, viewportHeight: number): ArrowPosition => {
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;

  const horizontalSection = pickSection(centerX, viewportWidth);
  const verticalSection = pickSection(centerY, viewportHeight);

  const horizontal: HorizontalAnchor = horizontalSection === 0 ? 'left' : horizontalSection === 2 ? 'right' : 'center';
  const vertical: VerticalAnchor = verticalSection === 0 ? 'top' : verticalSection === 2 ? 'bottom' : 'center';

  const maxOffsetX = viewportWidth - rect.width;
  const maxOffsetY = viewportHeight - rect.height;

  const offsetX =
    horizontal === 'left'
      ? clampOffset(rect.left, maxOffsetX)
      : horizontal === 'right'
        ? clampOffset(viewportWidth - rect.right, maxOffsetX)
        : 0;

  const offsetY =
    vertical === 'top'
      ? clampOffset(rect.top, maxOffsetY)
      : vertical === 'bottom'
        ? clampOffset(viewportHeight - rect.bottom, maxOffsetY)
        : 0;

  return { horizontal, vertical, offsetX, offsetY };
};

const CENTER_OFFSET = `calc(50% - ${ARROW_SIZE / 2}px)`;

/** Renders a saved anchor as CSS positioning declarations for the fixed-position button. */
export const arrowPositionToCss = ({ horizontal, vertical, offsetX, offsetY }: ArrowPosition): string => {
  const horizontalRule =
    horizontal === 'left' ? `left: ${offsetX}px;` : horizontal === 'right' ? `right: ${offsetX}px;` : `left: ${CENTER_OFFSET};`;

  const verticalRule =
    vertical === 'top' ? `top: ${offsetY}px;` : vertical === 'bottom' ? `bottom: ${offsetY}px;` : `top: ${CENTER_OFFSET};`;

  return `${horizontalRule} ${verticalRule}`;
};

/** Renders the transient drag position (the button follows the pointer via absolute left/top). */
export const liveDragToCss = (x: number, y: number): string => `left: ${x}px; top: ${y}px;`;

const HORIZONTAL_ANCHORS: ReadonlyArray<HorizontalAnchor> = ['left', 'center', 'right'];
const VERTICAL_ANCHORS: ReadonlyArray<VerticalAnchor> = ['top', 'center', 'bottom'];

/** Guards against malformed values read back from localStorage. */
export const isArrowPosition = (value: unknown): value is ArrowPosition => {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    HORIZONTAL_ANCHORS.includes(candidate.horizontal as HorizontalAnchor) &&
    VERTICAL_ANCHORS.includes(candidate.vertical as VerticalAnchor) &&
    typeof candidate.offsetX === 'number' &&
    typeof candidate.offsetY === 'number'
  );
};
